import {
  MobileRow,
  MobileRowTitle,
  MobileRowValue,
  MobileTableRowWrap,
  MobileTableWrapper,
  NoResults
} from '@/components/table/mobileTable/mobileTable';

import { CompliancePageDealDocumentsEmailTrackingListQuery } from '@/__generated__/graphql';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { Tooltip } from '@/components/basicComponents/tooltip';
import { TableColumns } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { getUTCDate } from '@/shared/utils/date';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import { TenantType } from '@trace-8/trace8-schema';
import _ from 'lodash';
import styled from 'styled-components';
import { dealDocumentsTrackingTableEmailTrackingCellTooltips } from '../../constants';
import { getDealDocumentsTrackingTableDocsSentCellValue } from './table';

interface DealDocumentsTrackingMobileTableProps {
  tableColumns: TableColumns[];
  tableRows: CompliancePageDealDocumentsEmailTrackingListQuery['dealDocumentsEmailTrackingList']['dealDocumentTrackingData'];
  openPage: (path: string) => void;
}

const DealDocumentsTrackingMobileTable = ({ tableColumns, tableRows, openPage }: DealDocumentsTrackingMobileTableProps) => {
  const { user } = useAuth();

  const TableRow = ({
    el
  }: {
    el: CompliancePageDealDocumentsEmailTrackingListQuery['dealDocumentsEmailTrackingList']['dealDocumentTrackingData'][number];
  }) => {
    return (
      <MobileTableRowWrap>
        <MobileRow>
          <MobileRowTitle>{tableColumns[0].title}</MobileRowTitle>
          <MobileRowValue
            onClick={() =>
              openPage(
                `/investors/${el?.allocation?.legalEntity?.family?.id}/${el?.allocation?.legalEntity?.id}?backToTitle=Send Deal Documents Tracking`
              )
            }
            isRedirect
          >
            {el?.allocation?.legalEntity?.entityName}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[1].title}</MobileRowTitle>
          <MobileRowValue
            onClick={() => openPage(`/strategyDetails/${el?.allocation?.investment?.id}?backToTitle=Send Deal Documents Tracking`)}
            isRedirect
          >
            {el?.allocation?.investment?.name}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[2].title}</MobileRowTitle>
          <MobileRowValue>{el?.action}</MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[3].title}</MobileRowTitle>
          <MobileRowValue>{!!el?.actionDate && getUTCDate(el?.actionDate).toUpperCase()}</MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[4].title}</MobileRowTitle>
          <MobileRowValue>{el?.actionPerformedBy}</MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[5].title}</MobileRowTitle>
          <MobileRowValue>{el?.emailRecipient}</MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[6].title}</MobileRowTitle>
          <MobileRowValue>{getDealDocumentsTrackingTableDocsSentCellValue(el?.assetFileNames)}</MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[7].title}</MobileRowTitle>
          <MobileRowValue>
            {!!el?.emailTrackingStatus && (
              <EmailTrackingStatusCellWrapper>
                {_.startCase(el?.emailTrackingStatus.toLowerCase())}
                <Tooltip
                  title={dealDocumentsTrackingTableEmailTrackingCellTooltips[el?.emailTrackingStatus]}
                  disableTooltip={!dealDocumentsTrackingTableEmailTrackingCellTooltips[el?.emailTrackingStatus]}
                >
                  <InfoIcon fill={themeVar('font.weak')} style={{ position: 'relative', top: '3px' }} />
                </Tooltip>
              </EmailTrackingStatusCellWrapper>
            )}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[8].title}</MobileRowTitle>
          <MobileRowValue>{el?.allocation?.operationsWorkflowTransitionTask?.name}</MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[9].title}</MobileRowTitle>
          <MobileRowValue>{!!el?.allocation?.lastUpdatedAt && getUTCDate(el?.allocation?.lastUpdatedAt).toUpperCase()}</MobileRowValue>
        </MobileRow>

        {user && user.tenant.type === TenantType.PLATFORM_MANAGER && (
          <MobileRow>
            <MobileRowTitle>{tableColumns[10].title}</MobileRowTitle>
            <MobileRowValue>{el?.allocation?.tenant?.name}</MobileRowValue>
          </MobileRow>
        )}
      </MobileTableRowWrap>
    );
  };

  return (
    <MobileTableWrapper>
      {tableRows.length ? (
        tableRows.map((el) => {
          // eslint-disable-next-line react/prop-types
          return <TableRow el={el} key={guid()} />;
        })
      ) : (
        <NoResults>No Results</NoResults>
      )}
    </MobileTableWrapper>
  );
};

export default DealDocumentsTrackingMobileTable;

const EmailTrackingStatusCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
